import {
  Avatar,
  Badge,
  Box,
  Group,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import type { ReactNode } from 'react';
import { AlertTriangle } from 'react-feather';
import type {
  AbsentReason,
  AttendanceMarkingStatus,
} from '../../../../services/api/coachPortal';
import {
  getAvatarColor,
  getInitialsFromParts,
} from '../../../../utils/avatarHelpers';
import { getPreferredParenthetical } from '../../../../utils/participantDisplayName';
import type { AttendanceDraft } from '../../../../hooks/useCoachPostSession';
import { ENGAGEMENT_RATING_CONFIG } from '../../../../config/engagementRatingConfig';

/**
 * MED-979 / MED-1053 — per-participant row inside the AttendanceEngagementCard
 * (shared with the peer-leader card).
 *
 *   - Top row: avatar + name on the left, Present/Partial/Absent on the right.
 *   - When a status is selected, an expansion panel drops below with label-left
 *     field rows:
 *       Present → Engagement + Notes
 *       Partial → Reason + Engagement + Notes
 *       Absent  → Reason + Notes (no Engagement)
 *
 * MED-1053 polishes the treatment to Figma: rounded status pills with the
 * Present (green) / Partial (amber) / Absent (red) selected states; navy
 * Reason / Engagement chips; a #FAFBFC expansion box; a multi-line Notes field.
 * Behavior (status clearing AC7, conditional expansion, the peer-leader "You"
 * badge) is unchanged.
 */

/**
 * MED-1191 AC9 — the pre-fill header note. Shown on both leader-facing cards
 * (coach + peer leader) only when the roster arrived pre-populated, so the
 * leader knows the statuses are suggestions to review. Kept here so the copy
 * stays identical across both entry points.
 */
export const ATTENDANCE_PREFILL_NOTE =
  "Attendance is pre-filled based on each participant's presence in the SGOS session. Review and adjust as needed.";

interface ReasonOption {
  value: AbsentReason;
  label: string;
}

// MED-979 AC4: the new reason taxonomy. Same set for Partial and Absent.
const REASON_OPTIONS: ReasonOption[] = [
  { value: 'business_conflict', label: 'Business Conflict' },
  { value: 'personal_conflict', label: 'Personal Conflict' },
  { value: 'no_notice', label: 'No Notice' },
];

const NOTES_PLACEHOLDER = 'Anything additional about how they showed up?';
const NOTES_MAX_LENGTH = 5000;

// MED-1053 — Figma status-pill palettes (selected states).
const PRESENT_PALETTE = { bg: '#ECFDF5', border: '#A7F3D0', text: '#059669' };
const PARTIAL_PALETTE = { bg: '#FEF3E2', border: '#F59E0B', text: '#F59E0B' };
const ABSENT_PALETTE = { bg: '#FEE2E2', border: '#FECACA', text: '#DC2626' };
const UNSELECTED = { bg: '#FFFFFF', border: '#E5E7EB', text: '#9CA3AF' };

const CHIP_ON = '#1B2B3A'; // selected Reason / Engagement chip
const PANEL_BG = '#FAFBFC';
const PANEL_BORDER = '#F0F0EE';
const LABEL_COLOR = '#6B7280';
const NAME_COLOR = '#1A1A2E';

interface Props {
  userId: string;
  firstName: string;
  lastName: string;
  /**
   * MED-1162 (AC5) — preferred name when set. This is a record/administrative
   * surface, so it keeps the full formal name and appends the preferred name in
   * a dimmed parenthetical (`Robert Smith (Bob)`) when it differs.
   */
  preferredName?: string | null;
  draft: AttendanceDraft;
  onChange: (next: AttendanceDraft) => void;
  /**
   * MED-817 AC5 — render a "You" badge next to the name. Used on the peer
   * leader post-session screen where the leader appears in their own
   * participant list. The coach view never sets this (no self-row).
   *
   * MED-1163 — when this is the leader's own row, we also suppress the
   * engagement-rating selector and the notes field and render a static
   * "Session Leader" label in their place. The leader is not asked to rate
   * their own engagement (AC1/AC2); their attendance status is unchanged (AC3).
   */
  isYou?: boolean;
  /**
   * MED-1267 (AC22) — the participant's CURRENT cohort status. When 'withdrawn'
   * the row carries E3-15's Universal Visual Treatment (WITHDRAWN pill + 50%
   * opacity). This surface only ever receives a withdrawn participant for a
   * session they were enrolled for at the time (getSessionParticipants' historical
   * rule), so the coach can still legitimately mark their attendance (AC4) while
   * seeing that they have since left the program.
   */
  participantStatus?: string | null;
}

export function ParticipantAttendanceRow({
  userId,
  firstName,
  lastName,
  preferredName,
  draft,
  onChange,
  isYou = false,
  participantStatus,
}: Props) {
  const fullName = `${firstName} ${lastName}`.trim();
  const isWithdrawn = participantStatus === 'withdrawn';
  // MED-1162 (AC5) — dimmed "(Bob)" appended when a preferred name differs from
  // the formal first name; null otherwise (no stray parens, AC7).
  const preferredParenthetical = getPreferredParenthetical(preferredName, firstName);
  const initials = getInitialsFromParts(firstName, lastName);
  const color = getAvatarColor(userId);

  const setStatus = (next: AttendanceMarkingStatus) => {
    // MED-979 AC7: clear values that don't apply to the new status. Notes are
    // status-agnostic per AC5 and stay across status changes.
    // MED-1191 AC8: picking any status is the leader reviewing the row, so the
    // auto-population marker (and its "Review" flag) always clears here.
    if (next === 'attended') {
      onChange({
        status: 'attended',
        absent_reason: null,
        rating: draft.rating,
        coach_notes: draft.coach_notes,
        autoPopulated: false,
      });
    } else if (next === 'partial') {
      onChange({
        status: 'partial',
        absent_reason: draft.absent_reason,
        rating: draft.rating,
        coach_notes: draft.coach_notes,
        autoPopulated: false,
      });
    } else {
      onChange({
        status: 'absent',
        absent_reason: draft.absent_reason,
        rating: null,
        coach_notes: draft.coach_notes,
        autoPopulated: false,
      });
    }
  };

  const setReason = (value: AbsentReason) => {
    onChange({ ...draft, absent_reason: value });
  };

  const setRating = (rating: number) => {
    onChange({ ...draft, rating });
  };

  const setNotes = (notes: string) => {
    onChange({ ...draft, coach_notes: notes });
  };

  const showReason = draft.status === 'partial' || draft.status === 'absent';
  // MED-1163 AC1: the leader's own row never shows the engagement selector or
  // the notes field. Reason stays available for Partial/Absent (it's attendance
  // information, not a self-engagement rating).
  const showEngagement =
    !isYou && (draft.status === 'attended' || draft.status === 'partial');
  const showNotes = !isYou;
  // For the leader's row the only panel content is Reason (Partial/Absent), so a
  // Present leader collapses to a single line with the "Session Leader" label.
  const showPanel = isYou ? showReason : draft.status !== null;
  // MED-1191 AC8: a light "Review" flag on rows auto-set to Partial (the only
  // status inferred against the 5-minute threshold, so the most likely to need
  // correction). Present/Absent auto rows are shown plain. Never on the leader's
  // own row, whose status is not heartbeat-inferred (AC5).
  const showReviewFlag = !isYou && draft.autoPopulated && draft.status === 'partial';

  return (
    // MED-1267 (AC22) — E3-15 Universal Visual Treatment: dim the whole row to
    // 50% when the participant has since withdrawn. Attendance controls stay
    // interactive (AC4 — a coach may still mark a session the person attended
    // before leaving).
    <Stack gap={8} style={isWithdrawn ? { opacity: 0.5 } : undefined}>
      {/* Base row — avatar + name + Present/Partial/Absent triplet */}
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap={10} align="center" wrap="nowrap">
          <Avatar color={color} radius="xl" size={32}>
            {initials}
          </Avatar>
          <Group gap={6} align="center" wrap="nowrap">
            <Text fz={13} fw={500} c={NAME_COLOR}>
              {fullName}
              {preferredParenthetical && (
                <Text span c="dimmed" fw={400}>
                  {' '}
                  ({preferredParenthetical})
                </Text>
              )}
            </Text>
            {/* MED-1267 (AC22) — WITHDRAWN pill, matching the E3-15 treatment
                used on MarkAttendanceModal (#E8E8E8 / #444444, uppercase). */}
            {isWithdrawn && (
              <Badge
                size="xs"
                variant="filled"
                styles={{
                  root: {
                    backgroundColor: '#E8E8E8',
                    color: '#444444',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    fontWeight: 600,
                  },
                }}
              >
                Withdrawn
              </Badge>
            )}
            {isYou && (
              <Box
                style={{
                  backgroundColor: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  borderRadius: 3,
                  padding: '1px 7px',
                }}
              >
                <Text fz={10} fw={700} c="#3D7A4A">
                  You
                </Text>
              </Box>
            )}
            {/* MED-1163 AC2: static, non-editable "Session Leader" label shown
                alongside the leader's name in place of the engagement selector
                and notes field. */}
            {isYou && (
              <Box
                style={{
                  backgroundColor: '#EEF2F6',
                  border: '1px solid #D6DEE6',
                  borderRadius: 3,
                  padding: '1px 7px',
                }}
              >
                <Text fz={10} fw={700} c="#1B2B3A">
                  Session Leader
                </Text>
              </Box>
            )}
            {/* MED-1191 AC8: "Review" flag on auto-inferred Partial rows. Amber
                warning treatment (design-system warningAmber family). */}
            {showReviewFlag && (
              <Group
                gap={3}
                align="center"
                wrap="nowrap"
                style={{
                  backgroundColor: '#FCF0D4',
                  border: '1px solid #F0D28A',
                  borderRadius: 3,
                  padding: '1px 7px',
                }}
              >
                <AlertTriangle size={11} color="#92650E" aria-hidden="true" />
                <Text fz={10} fw={700} c="#92650E">
                  Review
                </Text>
              </Group>
            )}
          </Group>
        </Group>

        <Group gap={4} wrap="nowrap">
          <StatusPill
            label="Present"
            active={draft.status === 'attended'}
            palette={PRESENT_PALETTE}
            onClick={() => setStatus('attended')}
          />
          <StatusPill
            label="Partial"
            active={draft.status === 'partial'}
            palette={PARTIAL_PALETTE}
            onClick={() => setStatus('partial')}
          />
          <StatusPill
            label="Absent"
            active={draft.status === 'absent'}
            palette={ABSENT_PALETTE}
            onClick={() => setStatus('absent')}
          />
        </Group>
      </Group>

      {/* MED-979 AC1 — expansion panel below the row. */}
      {showPanel && (
        <Box
          style={{
            backgroundColor: PANEL_BG,
            border: `1px solid ${PANEL_BORDER}`,
            borderRadius: 8,
            padding: '15px 13px',
          }}
        >
          <Stack gap={16}>
            {showReason && (
              <FieldRow label="Reason">
                {REASON_OPTIONS.map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    active={draft.absent_reason === opt.value}
                    onClick={() => setReason(opt.value)}
                  />
                ))}
              </FieldRow>
            )}

            {showEngagement && (
              <FieldRow label="Engagement">
                {ENGAGEMENT_RATING_CONFIG.map(rating => (
                  <Chip
                    key={rating.value}
                    label={rating.label}
                    active={draft.rating === rating.value}
                    onClick={() => setRating(rating.value)}
                    ariaLabel={`Engagement: ${rating.label}`}
                  />
                ))}
              </FieldRow>
            )}

            {showNotes && (
              <FieldRow label="Notes" sublabel="(Optional)">
                <Textarea
                  placeholder={NOTES_PLACEHOLDER}
                  value={draft.coach_notes ?? ''}
                  onChange={event => setNotes(event.currentTarget.value)}
                  maxLength={NOTES_MAX_LENGTH}
                  aria-label={`Notes for ${fullName}`}
                  autosize
                  minRows={2}
                  style={{ flex: 1 }}
                  styles={{
                    input: {
                      borderColor: '#E5E7EB',
                      borderRadius: 6,
                      fontSize: 13,
                    },
                  }}
                />
              </FieldRow>
            )}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

/**
 * MED-1053 — rounded status pill (Present / Partial / Absent). `palette` is the
 * selected-state color set; unselected is the shared gray scheme.
 */
function StatusPill({
  label,
  active,
  palette,
  onClick,
}: {
  label: string;
  active: boolean;
  palette: { bg: string; border: string; text: string };
  onClick: () => void;
}) {
  const c = active ? palette : UNSELECTED;
  return (
    <UnstyledButton
      onClick={onClick}
      data-active={active ? 'true' : 'false'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 13px',
        borderRadius: 6,
        border: `1px solid ${c.border}`,
        backgroundColor: c.bg,
        color: c.text,
        fontSize: 11,
        fontWeight: active ? 700 : 600,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </UnstyledButton>
  );
}

/** MED-1053 — Reason / Engagement chip: navy when selected, white otherwise. */
function Chip({
  label,
  active,
  onClick,
  ariaLabel,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  ariaLabel?: string;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      aria-label={ariaLabel}
      data-active={active ? 'true' : 'false'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 7px',
        borderRadius: 6,
        border: `1px solid ${active ? CHIP_ON : '#E5E7EB'}`,
        backgroundColor: active ? CHIP_ON : '#FFFFFF',
        color: active ? '#FFFFFF' : '#9CA3AF',
        fontSize: 11,
        fontWeight: active ? 700 : 600,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </UnstyledButton>
  );
}

/**
 * Field row inside the expansion panel — bold label on the left (fixed width),
 * chips / input on the right.
 */
function FieldRow({
  label,
  sublabel,
  children,
}: {
  label: string;
  sublabel?: string;
  children: ReactNode;
}) {
  return (
    <Group gap={18} align={sublabel ? 'flex-start' : 'center'} wrap="nowrap">
      <Box style={{ width: 80, flexShrink: 0 }}>
        <Text fz={11} fw={700} c={LABEL_COLOR}>
          {label}
        </Text>
        {sublabel && (
          <Text fz={11} fw={500} fs="italic" c={LABEL_COLOR}>
            {sublabel}
          </Text>
        )}
      </Box>
      <Group gap={6} wrap="wrap" style={{ flex: 1 }}>
        {children}
      </Group>
    </Group>
  );
}

export default ParticipantAttendanceRow;
